import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { MessageSquare, Send } from "lucide-react";
import { AppButton } from "../ui";
import VideoCommentItem from "./VideoCommentItem";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import "./Videos.scss";

const VideoComments = ({ videoId, role }) => {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState("");
  const limit = 20;

  const user = getCurrentUser();
  const currentUserId = user?.id || user?._id;

  const fetchComments = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/videos/${videoId}/comments`, {
        params: { page: pageNum, limit },
      });
      if (data.success) {
        setComments((prev) => (append ? [...prev, ...data.data] : data.data));
        setTotal(data.pagination?.total || 0);
      }
    } catch {
      // silently fail
    }
    setLoading(false);
  }, [videoId]);

  useEffect(() => {
    if (videoId) {
      setPage(1);
      fetchComments(1, false);
    }
  }, [videoId, fetchComments]);

  const handlePost = async () => {
    if (!message.trim()) return;
    setPosting(true);
    try {
      const { data } = await apiClient.post(`/videos/${videoId}/comments`, {
        message: message.trim(),
      });
      if (data.success) {
        setComments((prev) => [data.data, ...prev]);
        setTotal((t) => t + 1);
        setMessage("");
      }
    } catch {
      // silently fail
    }
    setPosting(false);
  };

  const handleDelete = async (commentId) => {
    try {
      const { data } = await apiClient.delete(`/videos/comments/${commentId}`);
      if (data.success) {
        setComments((prev) => prev.filter((c) => c._id !== commentId));
        setTotal((t) => t - 1);
      }
    } catch {
      // silently fail
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchComments(nextPage, true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  const hasMore = comments.length < total;

  return (
    <div className="video-comments">
      <div className="video-comments__header">
        <MessageSquare size={16} />
        <h6>Comments</h6>
        {total > 0 && <span>({total})</span>}
      </div>

      {/* Comment Input */}
      <div className="video-comments__input-wrapper">
        <textarea
          rows={1}
          placeholder="Add a comment..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={posting}
        />
        <AppButton
          variant="default"
          size="icon"
          onClick={handlePost}
          disabled={!message.trim() || posting}
        >
          {posting ? <Spinner animation="border" size="sm" /> : <Send size={16} />}
        </AppButton>
      </div>

      {/* Comment List */}
      <div className="video-comments__list">
        {comments.length === 0 && !loading && (
          <div className="video-comments__empty">No comments yet. Be the first!</div>
        )}

        {comments.map((comment) => (
          <VideoCommentItem
            key={comment._id}
            comment={comment}
            role={role}
            currentUserId={currentUserId}
            onDelete={handleDelete}
          />
        ))}

        {loading && (
          <div className="videos-spinner">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      {hasMore && !loading && (
        <div className="video-comments__load-more">
          <AppButton variant="ghost" size="sm" onClick={handleLoadMore}>
            Load more comments
          </AppButton>
        </div>
      )}
    </div>
  );
};

export default VideoComments;
